"use client";

import { useId, useState, type FormEvent } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

type ClaimErrorBody = { error?: { message?: string } };

type FormValues = {
  contactName: string;
  contactTitle: string;
  contactPhone: string;
  contactEmail: string;
};

/**
 * "Is this your firm?" action on a public profile — CONTRACTS §5 (D owns
 * the route: `POST /api/firms/[id]/claim`). Click opens a dialog collecting
 * claimant contact details (name, title at the firm, phone, email) instead
 * of firing the POST immediately. Handles 401 (redirect to sign-in), 201
 * (success toast + close), 409 (inline error inside the dialog).
 */
export function ClaimButton({
  firmId,
  userEmail,
  className,
}: {
  firmId: string;
  userEmail: string;
  className?: string;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const formId = useId();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [values, setValues] = useState<FormValues>({
    contactName: "",
    contactTitle: "",
    contactPhone: "",
    contactEmail: userEmail,
  });

  function set<K extends keyof FormValues>(key: K, value: FormValues[K]) {
    setValues((v) => ({ ...v, [key]: value }));
  }

  function handleOpenChange(next: boolean) {
    if (pending) return;
    setOpen(next);
    if (next) {
      setError(null);
      setValues({
        contactName: "",
        contactTitle: "",
        contactPhone: "",
        contactEmail: userEmail,
      });
    }
  }

  const canSubmit =
    values.contactName.trim() !== "" &&
    values.contactTitle.trim() !== "" &&
    values.contactPhone.trim() !== "" &&
    values.contactEmail.trim() !== "";

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (pending || !canSubmit) return;
    setPending(true);
    setError(null);
    try {
      const res = await fetch(`/api/firms/${firmId}/claim`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });

      if (res.status === 401) {
        const qs = searchParams.toString();
        const redirectTo = qs ? `${pathname}?${qs}` : pathname;
        router.push(`/sign-in?redirect_url=${encodeURIComponent(redirectTo)}`);
        return;
      }

      if (res.status === 201) {
        toast.success("Claim submitted — an administrator will review it");
        setOpen(false);
        return;
      }

      if (res.status === 409) {
        const body = (await res.json().catch(() => null)) as ClaimErrorBody | null;
        setError(
          body?.error?.message ??
            "You already have a pending claim, or this firm is already claimed."
        );
        return;
      }

      const body = (await res.json().catch(() => null)) as ClaimErrorBody | null;
      setError(body?.error?.message ?? "Could not submit claim. Try again.");
    } catch {
      setError("Could not submit claim — check your connection and try again.");
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button className={cn(className)}>Claim this firm</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Claim this firm</DialogTitle>
          <DialogDescription>
            Tell us who you are so an administrator can verify the request.
            We&apos;ll review it before your account takes ownership of this
            profile.
          </DialogDescription>
        </DialogHeader>

        <form id={formId} onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor={`${formId}-name`}>Name</Label>
            <Input
              id={`${formId}-name`}
              value={values.contactName}
              onChange={(e) => set("contactName", e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${formId}-title`}>
              Title
              <span className="font-normal text-muted-foreground">
                — your title at the firm
              </span>
            </Label>
            <Input
              id={`${formId}-title`}
              placeholder="e.g. Managing Director"
              value={values.contactTitle}
              onChange={(e) => set("contactTitle", e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${formId}-phone`}>Phone</Label>
            <Input
              id={`${formId}-phone`}
              type="tel"
              value={values.contactPhone}
              onChange={(e) => set("contactPhone", e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${formId}-email`}>Email</Label>
            <Input
              id={`${formId}-email`}
              type="email"
              value={values.contactEmail}
              onChange={(e) => set("contactEmail", e.target.value)}
              required
            />
          </div>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
        </form>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={pending}
          >
            Cancel
          </Button>
          <Button type="submit" form={formId} disabled={pending || !canSubmit}>
            {pending ? "Submitting…" : "Submit claim"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
